import type { UseFormRegisterReturn } from 'react-hook-form';
import { FormField, Input } from '@/shared/components';
import type { BookableResourceResponse } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';
import { decimalsFor, priceState } from './resourcePriceModel';

/**
 * Brutto-Stundenpreis mit der Grenze, die der Server prüft: 0 ist kostenlos, sonst muss schon eine
 * halbe Stunde den Mindestbetrag der Online-Zahlung erreichen. Die Vorschau nennt den Betrag der
 * kürzesten Auswahl so, wie der Gast ihn sieht.
 */
export function ResourcePriceField({
  registration,
  hourlyPrice,
  currency,
  minimum,
  resource,
  error,
}: {
  registration: UseFormRegisterReturn;
  hourlyPrice: number;
  currency: string;
  minimum: number | null;
  resource?: BookableResourceResponse;
  error?: string;
}) {
  const state = priceState(hourlyPrice, currency, minimum);
  const money = (amount: number, code = currency) => formatMoney(amount, code);
  const previous =
    resource?.priceCurrencyMismatch && resource.priceCurrency
      ? `Bisher ${money(resource.hourlyPrice, resource.priceCurrency)} je Stunde – das Objekt rechnet inzwischen in ${currency}. Bitte den Preis in ${currency} neu eingeben.`
      : undefined;
  const hints: Record<string, string | undefined> = {
    missing: previous ?? 'Preis eingeben; 0 heißt kostenlos.',
    free:
      minimum === null
        ? `Kostenlos. In ${currency} sind nur kostenlose Ressourcen möglich, weil kein Mindestbetrag der Online-Zahlung hinterlegt ist.`
        : `Kostenlos – ohne Zahlung sofort bestätigt. Ein Preis braucht mindestens ${money(minimum)} je halbe Stunde.`,
    payable:
      state.kind === 'payable' && minimum !== null
        ? `Eine halbe Stunde kostet ${money(state.halfHour)}. Mindestbetrag ${money(minimum)}.`
        : undefined,
  };
  const problems: Record<string, string | undefined> = {
    belowMinimum:
      state.kind === 'belowMinimum'
        ? `Eine halbe Stunde ergäbe ${money(state.halfHour)} – das liegt unter dem Mindestbetrag von ${money(state.minimum)}.`
        : undefined,
    tooPrecise:
      state.kind === 'tooPrecise'
        ? state.decimals === 0
          ? `In ${currency} gibt es keine Nachkommastellen.`
          : `Höchstens ${state.decimals} Nachkommastellen in ${currency}.`
        : undefined,
    currencyUnsupported: `In ${currency} sind nur kostenlose Ressourcen möglich.`,
  };

  return (
    <FormField
      label={`Preis je Stunde (${currency}, brutto)`}
      error={error ?? problems[state.kind]}
      hint={hints[state.kind]}
    >
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        step={10 ** -decimalsFor(currency)}
        // Nur ein Objekt ohne bekannte Grenze, dessen Ressource schon kostenlos ist, sperrt das Feld —
        // aus dem Stand beim Öffnen, nie aus der laufenden Eingabe.
        disabled={minimum === null && (resource?.hourlyPrice ?? 0) === 0}
        {...registration}
      />
    </FormField>
  );
}
